import React, { useState, useRef, useEffect } from 'react';

/**
 * EditableCell — Click-to-edit table cell.
 * Shows plain text by default; click to enter edit mode.
 * Enter/blur saves, Escape cancels.
 */
export default function EditableCell({ value, recordId, fieldKey, onSave, className = '' }) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(value || '');
    const inputRef = useRef(null);

    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editing]);

    // Sync external value changes
    useEffect(() => {
        if (!editing) setDraft(value || '');
    }, [value, editing]);

    const handleSave = () => {
        setEditing(false);
        const trimmed = draft.trim();
        if (trimmed !== (value || '').trim()) {
            onSave(recordId, fieldKey, trimmed);
        }
    };

    const handleCancel = () => {
        setEditing(false);
        setDraft(value || '');
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') handleSave();
        else if (e.key === 'Escape') handleCancel();
        else if (e.key === 'Tab') {
            e.preventDefault();
            handleSave();
        }
    };

    if (editing) {
        return (
            <input
                ref={inputRef}
                type="text"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onBlur={handleSave}
                onKeyDown={handleKeyDown}
                className={`w-full px-1.5 py-0.5 text-sm border border-stone-300 rounded focus:outline-none focus:ring-1 focus:ring-stone-900/30 bg-white ${className}`}
            />
        );
    }

    return (
        <span
            onClick={() => setEditing(true)}
            className={`cursor-pointer hover:bg-stone-100 rounded px-1.5 py-0.5 -mx-1.5 -my-0.5 inline-block min-w-[20px] min-h-[20px] transition-colors ${className}`}
            title="Click to edit"
        >
            {value || <span className="text-stone-300">—</span>}
        </span>
    );
}
